import React from 'react'
import PropTypes from 'prop-types'
import { Alertbox, ErrorLink } from '@olxbr/ds-web'
import { AlertTriangle } from '@olxbr/ds-icons'
import styled from 'styled-components'

import {
  cadastroLink,
  accountReactivationLink,
  esqueciMinhaSenha,
  faleConosco,
} from '../../../settings/staticUrl'

const getHref = ({
  showAccountNotFoundError,
  showInactiveError,
  showPasswordMismatchError,
  contactUsLink,
}) => {
  if (showAccountNotFoundError) return cadastroLink
  if (showInactiveError) return accountReactivationLink
  if (showPasswordMismatchError) return esqueciMinhaSenha
  return contactUsLink || faleConosco
}

const ErrorWrapper = styled.div`
  a {
    text-decoration: none;
    display: inline;
    font-size: var(--font-size-1-75);
  }
`

const ErrorMessage = styled.p.attrs({ className: 'typo-body-small' })`
  display: inline;
  color: inherit;
`

const getGenericInfo = ({
  showContactUs,
  showAccountNotFoundError,
  showPasswordMismatchError,
  showGoogleInitializationError,
  showEmailNotFoundError,
  showBiometryError,
  showRateLimitMagicLink,
  showDeleteAccount,
  showInactiveError,
}) => {
  if (showAccountNotFoundError) {
    return { showInfo: true, info: 'Não tem conta? ', linkText: 'Cadastre-se.' }
  }

  if (showRateLimitMagicLink) {
    return {
      showInfo: true,
      info: 'Podemos enviar um e-mail para ajudar a acessar sua conta. ',
      linkText: 'Enviar acesso rápido',
    }
  }

  if (showPasswordMismatchError) {
    return {
      showInfo: true,
      info: 'Esqueceu sua senha? ',
      linkText: 'Crie uma nova senha.',
    }
  }

  if (showGoogleInitializationError) {
    return {
      showInfo: true,
      info: 'Tente outra opcão ou ',
      linkText: 'fale conosco.',
    }
  }

  if (showEmailNotFoundError) {
    return {
      showInfo: true,
      info: 'Digite novamente ou tente outro e-mail.',
      linkText: '',
    }
  }

  if (showBiometryError) {
    return {
      showInfo: true,
      info: 'Tente novamente com a senha.',
      linkText: '',
    }
  }

  if (showDeleteAccount) {
    return {
      showInfo: true,
      info: 'Para excluir sua conta, ',
      linkText: 'fale conosco.',
    }
  }

  if (showInactiveError) {
    return {
      showInfo: true,
      info: 'Precisa de ajuda? ',
      linkText: 'Reative a sua conta',
    }
  }

  if (showContactUs) {
    return {
      showInfo: true,
      info: 'Em caso de dúvidas, ',
      linkText: 'Fale com a gente.',
    }
  }

  return { showInfo: false }
}

export const GenericErrorBox = props => {
  const {
    contactUsLink,
    errorAction,
    info,
    linkText,
    showAccountNotFoundError,
    showBiometryError,
    showContactUs,
    showDeleteAccount,
    showEmailNotFoundError,
    showGoogleInitializationError,
    showInactiveError,
    showPasswordMismatchError,
    showRateLimitMagicLink,
    text,
  } = props

  let showInfo = false
  let customInfo = ''
  let customLinkText = ''
  let href = ''
  // If the info property is provided, the error message can be customized
  if (info || linkText) {
    showInfo = true
    customInfo = info
    customLinkText = linkText
  } else {
    // Get predefined error message
    const {
      showInfo: _showInfo,
      info: _info,
      linkText: _linkText,
    } = getGenericInfo({
      showContactUs,
      showAccountNotFoundError,
      showPasswordMismatchError,
      showGoogleInitializationError,
      showEmailNotFoundError,
      showBiometryError,
      showRateLimitMagicLink,
      showDeleteAccount,
      showInactiveError,
    })
    showInfo = _showInfo
    customInfo = _info
    customLinkText = _linkText
    href = getHref({
      showAccountNotFoundError,
      showInactiveError,
      showPasswordMismatchError,
      contactUsLink,
    })
  }

  return (
    <Alertbox variant="error" title={text} role="Alert" icon={<AlertTriangle />}>
      <ErrorWrapper>
        {showInfo && (
          <ErrorMessage>
            {customInfo}
            <ErrorLink href={href} onClick={errorAction}>
              {customLinkText}
            </ErrorLink>
          </ErrorMessage>
        )}
      </ErrorWrapper>
    </Alertbox>
  )
}

GenericErrorBox.defaultProps = {
  info: '',
  linkText: '',
  showContactUs: true,
}

GenericErrorBox.propTypes = {
  contactUsLink: PropTypes.string,
  errorAction: PropTypes.func,
  info: PropTypes.string,
  linkText: PropTypes.string,
  showAccountNotFoundError: PropTypes.bool,
  showBiometryError: PropTypes.bool,
  showContactUs: PropTypes.bool,
  showDeleteAccount: PropTypes.bool,
  showEmailNotFoundError: PropTypes.bool,
  showGoogleInitializationError: PropTypes.bool,
  showInactiveError: PropTypes.bool,
  showPasswordMismatchError: PropTypes.bool,
  showRateLimitMagicLink: PropTypes.bool,
  text: PropTypes.string.isRequired,
}
